/**
 * 거래처 상세 — 레거시 `clientInfoDetails.js` UI·저장·나가기 가드 포팅.
 */
import {
  useBusinessRelationshipDetail,
  useUpdateBusinessRelationship,
  useUpdateBusinessRelationshipCompanies,
} from '@/entities/business/hooks/useBusinessRelationships';
import { useUploadHospitalBusinessLicense } from '@/entities/hospital/hooks/useHospitals';
import { usePartnerCompanyOptions } from '@/entities/partner-company/hooks/usePartnerCompanies';
import { useMenuAccess } from '@/layouts/MenuAccessContext';
import { ClientDrugSelectModal, extractDrugCode } from '@/pages/clients/ClientDrugSelectModal';
import {
  DRUG_APPROVAL_STATUS,
  DRUG_APPROVAL_STATUS_OPTIONS,
  mapDrugApprovalToTransactionStatus,
  toDrugApprovalStatusValue,
  toPharmaTransactionStatusValue,
} from '@/pages/clients/clientStatusUtils';
import {
  TRANSACTION_STATUS_EDIT_OPTIONS,
  buildBusinessRelationshipCompaniesRequest,
  filterSettlementCompanyOptions,
  formatRelatedPharmacyLabel,
  formatStatusLabel,
  getBusinessRelationshipCompanyErrorMessage,
  resolveCompanyIdsWithDefaults,
  resolveSettlementCompanyIdAfterParentChange,
} from '@/pages/clients/clientUtils';
import { getCurrentRefYm } from '@/pages/fee-rates/feeRateUtils';
import { DownloadWithPreview } from '@/shared/components/DownloadWithPreview';
import { FileUploadButton } from '@/shared/components/FileUploadButton';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { FooterRow, Stack, ToolbarRow } from '@/shared/components/ui/row';
import { formatDate, formatDateTime } from '@/shared/lib/dateFormat';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { formatClosedOnDisplay } from '@/shared/utils/facilityClosure';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate, useParams, useRouter, useSearch } from '@tanstack/react-router';
import { Plus, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

type DrugListMode = 'available' | 'unavailable' | null;

const todayYmd = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatCodingMonth = (value?: string | null): string => {
  if (!value) return '-';
  const raw = String(value).trim();
  if (/^\d{6}$/.test(raw)) {
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}`;
  }
  return raw;
};

const formatAmountInput = (value: string): string => {
  if (!value) return '';
  const digits = value.replace(/[^\d]/g, '');
  if (!digits) return '';
  return Number(digits).toLocaleString('ko-KR');
};

const parseAmountInput = (value: string): string =>
  value.replace(/[^\d]/g, '');

const DrugCodeTags: React.FC<{
  codes: string[];
  onRemove: (code: string) => void;
  readOnly?: boolean;
}> = ({ codes, onRemove, readOnly }) => (
  <div className="flex min-h-10 flex-wrap gap-2 rounded-lg border p-3">
    {codes.length === 0 ? (
      <span className="text-sm text-muted-foreground">의약품 코드 없음</span>
    ) : (
      codes.map((code) => (
        <Badge key={code} variant="secondary" className="gap-1 pr-1">
          {code}
          {!readOnly ? (
            <button
              type="button"
              className="rounded p-0.5 hover:bg-muted"
              onClick={() => onRemove(code)}
              aria-label={`${code} 제거`}
            >
              <X className="h-3 w-3" />
            </button>
          ) : null}
        </Badge>
      ))
    )}
  </div>
);

export const ClientDetailPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const router = useRouter();
  const { readOnly } = useMenuAccess();
  const { clientId: clientIdParam } = useParams({ strict: false }) as {
    clientId?: string;
  };
  const search = useSearch({ strict: false }) as {
    drugCode?: string;
    type?: string;
  };
  const clientId = Number(clientIdParam);
  const drugCodeParam = search.drugCode?.trim() || undefined;

  const { data, isLoading, isError, error } = useBusinessRelationshipDetail(
    Number.isFinite(clientId) ? clientId : null,
    drugCodeParam,
  );
  const updateClient = useUpdateBusinessRelationship();
  const updateCompanies = useUpdateBusinessRelationshipCompanies();
  const uploadBusinessLicense = useUploadHospitalBusinessLicense();
  const { data: partnerCompanyOptions = [] } = usePartnerCompanyOptions();
  const businessLicenseInputRef = useRef<HTMLInputElement>(null);

  const [transactionStatus, setTransactionStatus] = useState('');
  const [approvalDate, setApprovalDate] = useState('');
  const [drugApprovalStatus, setDrugApprovalStatus] = useState('');
  const [drugApprovalDate, setDrugApprovalDate] = useState('');
  const [expectedAmount, setExpectedAmount] = useState('');
  const [expectedAmountEditable, setExpectedAmountEditable] = useState(true);
  const [targetDepartment, setTargetDepartment] = useState('');
  const [availableCodes, setAvailableCodes] = useState<string[]>([]);
  const [unavailableCodes, setUnavailableCodes] = useState<string[]>([]);
  const [parentCompanyId, setParentCompanyId] = useState('');
  const [settlementCompanyId, setSettlementCompanyId] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [drugModalMode, setDrugModalMode] = useState<DrugListMode>(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [hydratedRelationId, setHydratedRelationId] = useState<number | null>(
    null,
  );

  const refYm = getCurrentRefYm();

  // 데이터 도착 시 첫 페인트 전에 폼 상태 동기화 (Select controlled 경고 방지)
  if (data && hydratedRelationId !== data.relationPharmaCompanyId) {
    setHydratedRelationId(data.relationPharmaCompanyId);
    setTransactionStatus(
      toPharmaTransactionStatusValue(data.transactionStatus),
    );
    setApprovalDate(data.approvalDate?.slice(0, 10) ?? '');
    setDrugApprovalStatus(
      toDrugApprovalStatusValue(data.drugApprovalStatus),
    );
    setDrugApprovalDate(data.drugApprovalDate?.slice(0, 10) ?? '');
    setExpectedAmountEditable(
      data.totalMonthlyExpectedPrescriptionAmount != null,
    );
    setExpectedAmount(
      data.totalMonthlyExpectedPrescriptionAmount != null
        ? String(data.totalMonthlyExpectedPrescriptionAmount)
        : '',
    );
    setTargetDepartment(data.targetDepartment ?? '');
    setAvailableCodes(data.availableDrugCodes ?? []);
    setUnavailableCodes(data.unavailableDrugCodes ?? []);
    setParentCompanyId(
      data.parentCompanyId != null ? String(data.parentCompanyId) : '',
    );
    setSettlementCompanyId(
      data.settlementCompanyId != null ? String(data.settlementCompanyId) : '',
    );
    setIsDirty(false);
  }

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  // 서버가 null을 주면 루다링크(defaultOption)로 보여 준다. 더티로 치지 않는다.
  useEffect(() => {
    if (partnerCompanyOptions.length === 0) return;
    const resolved = resolveCompanyIdsWithDefaults(
      partnerCompanyOptions,
      parentCompanyId,
      settlementCompanyId,
    );
    if (resolved.parentCompanyId !== parentCompanyId) {
      setParentCompanyId(resolved.parentCompanyId);
    }
    if (resolved.settlementCompanyId !== settlementCompanyId) {
      setSettlementCompanyId(resolved.settlementCompanyId);
    }
  }, [parentCompanyId, partnerCompanyOptions, settlementCompanyId]);

  const markDirty = () => setIsDirty(true);

  const existingDrugCodesForModal = useMemo(() => {
    if (drugModalMode === 'available') {
      return [...availableCodes, ...unavailableCodes];
    }
    if (drugModalMode === 'unavailable') {
      return [...unavailableCodes, ...availableCodes];
    }
    return [];
  }, [availableCodes, drugModalMode, unavailableCodes]);

  /** 목록 URL 쿼리(검색 조건)를 유지하려면 히스토리 뒤로가기가 필요하다 */
  const goBack = () => {
    if (router.history.canGoBack()) {
      router.history.back();
      return;
    }
    navigate({ to: ROUTE_PATHS.CLIENTS } as never);
  };

  const handleExit = () => {
    if (isDirty) {
      setShowExitConfirm(true);
      return;
    }
    goBack();
  };

  const handleSave = async () => {
    if (!data) return;
    const showDrugApproval =
      data.clinicType === 'HOSPITAL' || data.drugCode != null;
    const resolvedCompanies = resolveCompanyIdsWithDefaults(
      partnerCompanyOptions,
      parentCompanyId,
      settlementCompanyId,
    );
    if (
      !resolvedCompanies.parentCompanyId ||
      !resolvedCompanies.settlementCompanyId
    ) {
      toast.error(
        '루다링크 업체가 등록되어 있지 않아 상위업체와 정산업체를 저장할 수 없습니다.',
      );
      setShowSaveConfirm(false);
      return;
    }

    try {
      const nextTransactionStatus =
        toPharmaTransactionStatusValue(transactionStatus);
      const nextDrugApprovalStatus =
        toDrugApprovalStatusValue(drugApprovalStatus);

      await updateClient.mutateAsync({
        relationPharmaCompanyId: data.relationPharmaCompanyId,
        data: {
          // 서버 fromKorean: 신청, 확인 중, 이관 확인 중, 승인, 거절, 재신청, 종료
          transactionStatus: nextTransactionStatus || undefined,
          // 레거시: 승인일 UI 없이, 승인 상태일 때만 날짜(없으면 오늘)
          approvalDate:
            nextTransactionStatus === '승인'
              ? approvalDate || todayYmd()
              : null,
          totalMonthlyExpectedPrescriptionAmount: expectedAmountEditable
            ? expectedAmount
              ? Number(expectedAmount)
              : null
            : undefined,
          availableDrugCodes: availableCodes,
          unavailableDrugCodes: unavailableCodes,
          ...(showDrugApproval
            ? {
                drugCode: data.drugCode ?? drugCodeParam ?? null,
                // PUT은 Jackson enum 이름. GET이 한글이어도 영문으로 보낸다.
                drugApprovalStatus: nextDrugApprovalStatus || undefined,
                drugApprovalDate:
                  nextDrugApprovalStatus === DRUG_APPROVAL_STATUS.APPROVED
                    ? drugApprovalDate || todayYmd()
                    : null,
              }
            : {}),
        },
      });
      await updateCompanies.mutateAsync({
        relationPharmaCompanyId: data.relationPharmaCompanyId,
        data: buildBusinessRelationshipCompaniesRequest({
          clinicType: data.clinicType,
          // 병원급: 목록에서 고른 행의 의약품 코드로 의약품 단위 지정
          drugCode: data.drugCode ?? drugCodeParam,
          parentCompanyId: Number(resolvedCompanies.parentCompanyId),
          settlementCompanyId: Number(resolvedCompanies.settlementCompanyId),
        }),
      });
      toast.success('고객 정보가 성공적으로 수정되었습니다');
      setIsDirty(false);
    } catch (e) {
      const companyError = (
        e as { response?: { data?: { code?: string } } }
      )?.response?.data?.code;
      if (
        companyError === 'DRUG_SCOPE_NOT_ALLOWED' ||
        companyError === 'BUSINESS_RELATIONSHIP_DRUG_NOT_FOUND'
      ) {
        toast.error(getBusinessRelationshipCompanyErrorMessage(e));
      } else {
        handleApiError(e, toast);
      }
    } finally {
      setShowSaveConfirm(false);
    }
  };

  const handleBusinessLicenseUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file || !data?.clinicId) return;

    try {
      await uploadBusinessLicense.mutateAsync({
        clinicId: data.clinicId,
        file,
        relationPharmaCompanyId: data.relationPharmaCompanyId,
        drugCode: drugCodeParam,
      });
      toast.success('병의원 사업자 등록증을 업로드했습니다.');
    } catch (err) {
      handleApiError(err, toast);
    } finally {
      if (businessLicenseInputRef.current) {
        businessLicenseInputRef.current.value = '';
      }
    }
  };

  const handleDrugModalConfirm = (labels: string[]) => {
    // 동일 코드는 `코드 | 명` 형식으로 덮어써 중복 태그를 방지
    const mergeLabels = (prev: string[]) => {
      const byCode = new Map(
        prev.map((entry) => [extractDrugCode(entry), entry] as const),
      );
      for (const label of labels) {
        const code = extractDrugCode(label);
        if (code) byCode.set(code, label);
      }
      return [...byCode.values()];
    };

    if (drugModalMode === 'available') {
      setAvailableCodes(mergeLabels);
    } else if (drugModalMode === 'unavailable') {
      setUnavailableCodes(mergeLabels);
    }
    markDirty();
    setDrugModalMode(null);
  };

  if (!Number.isFinite(clientId)) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        잘못된 거래처 ID입니다.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="py-16">
        <LoadingSpinner tip="거래처 정보를 불러오는 중..." />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        거래처 정보를 찾을 수 없습니다.
      </div>
    );
  }

  const isHospital = data.clinicType === 'HOSPITAL';
  const showDrugApprovalFields = isHospital || data.drugCode != null;
  const hasDepartments = (data.departments?.length ?? 0) > 0;
  const statusHistories = data.transactionStatusHistories ?? [];
  const businessLicenseImageUrl = data.businessLicenseImage?.trim() || '';
  const hasBusinessLicense = Boolean(businessLicenseImageUrl);
  const settlementOptions = filterSettlementCompanyOptions(
    partnerCompanyOptions,
    parentCompanyId,
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="거래처 신청 정보 상세 페이지"
        actions={
          <FooterRow gap={8}>
            <MenuWriteGate>
              <Button
                type="button"
                onClick={() => setShowSaveConfirm(true)}
                disabled={updateClient.isPending || !isDirty}
                className="ruda-button"
              >
                저장
              </Button>
            </MenuWriteGate>
            <Button type="button" variant="outline" onClick={handleExit}>
              나가기
            </Button>
          </FooterRow>
        }
      />

      <Card className="rounded-12 border-ruda-border-light">
        <CardContent className="space-y-6 p-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Field label="사용자 이메일">
              <Input disabled value={data.email || '-'} />
            </Field>
            <Field label="업체명">
              <Input disabled value={data.companyName || '-'} />
            </Field>
            <Field label="거래 신청일">
              <Input disabled value={formatDate(data.applicationDate)} />
            </Field>
            <Field label="거래처 재신청일">
              <Input
                disabled
                value={formatDate(data.resubmissionDate)}
              />
            </Field>
            <Field label="병의원 ID">
              <Input disabled value={String(data.clinicId ?? '-')} />
            </Field>
            <Field label="병의원명">
              <Input disabled value={data.clinicName || '-'} />
            </Field>
            <Field label="병의원 폐업일">
              <Input
                disabled
                value={formatClosedOnDisplay(data.clinicClosedOn)}
              />
            </Field>
            <Field label="병의원 구분코드">
              <Input disabled value={data.clinicTypeCode || '-'} />
            </Field>
            {hasBusinessLicense ? (
              <DownloadWithPreview
                label="병의원 사업자 등록증"
                src={businessLicenseImageUrl}
                downloadName={`${data.clinicName || '병의원'} 사업자 등록증`}
              />
            ) : (
              <div className="space-y-2">
                <Label>병의원 사업자 등록증</Label>
                <p className="text-sm text-muted-foreground">
                  등록된 파일이 없습니다.
                </p>
                {!readOnly ? (
                  <FileUploadButton
                    inputRef={businessLicenseInputRef}
                    accept="image/*,.pdf"
                    disabled={uploadBusinessLicense.isPending}
                    resetOnSelect={false}
                    onChange={handleBusinessLicenseUpload}
                  >
                    {uploadBusinessLicense.isPending
                      ? '업로드 중...'
                      : '파일 업로드'}
                  </FileUploadButton>
                ) : null}
              </div>
            )}
            <DownloadWithPreview
              label="병의원 인증확인서"
              src={data.clinicVerificationImage}
              downloadName="병의원 인증확인서"
            />

            {isHospital ? (
              <>
                <Field label="진료과">
                  {hasDepartments ? (
                    <Select
                      value={
                        data.targetDepartment || data.departments[0] || ''
                      }
                      disabled
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="진료과" />
                      </SelectTrigger>
                      <SelectContent>
                        {data.departments.map((dept) => (
                          <SelectItem key={dept} value={dept}>
                            {dept}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={targetDepartment}
                      onChange={(e) => {
                        setTargetDepartment(e.target.value);
                        markDirty();
                      }}
                      disabled={readOnly}
                    />
                  )}
                </Field>
                <Field label="주요 KOL 성함">
                  <Input disabled value={data.kolName || '-'} />
                </Field>
              </>
            ) : null}

            <Field label="제약사명">
              <Input disabled value={data.pharmaCompanyName || '-'} />
            </Field>
            <Field
              label="상위업체"
              hint="미선택 시 루다링크. 업체 관리에 등록된 재위탁 업체 중 선택합니다."
            >
              <Select
                value={parentCompanyId}
                disabled={readOnly}
                onValueChange={(value) => {
                  setParentCompanyId(value);
                  setSettlementCompanyId(
                    resolveSettlementCompanyIdAfterParentChange(
                      partnerCompanyOptions,
                      value,
                      settlementCompanyId,
                    ),
                  );
                  markDirty();
                }}
              >
                <SelectTrigger aria-label="상위업체">
                  <SelectValue placeholder="상위업체 선택" />
                </SelectTrigger>
                <SelectContent>
                  {partnerCompanyOptions.map((option) => (
                    <SelectItem
                      key={option.partnerCompanyId}
                      value={String(option.partnerCompanyId)}
                    >
                      {option.companyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field
              label="정산업체"
              hint="정산 수령처입니다. 상위업체가 없으면 루다링크, 있으면 해당 업체 또는 루다링크만 선택할 수 있습니다."
            >
              <Select
                value={settlementCompanyId}
                disabled={readOnly}
                onValueChange={(value) => {
                  setSettlementCompanyId(value);
                  markDirty();
                }}
              >
                <SelectTrigger aria-label="정산업체">
                  <SelectValue placeholder="정산업체 선택" />
                </SelectTrigger>
                <SelectContent>
                  {settlementOptions.map((option) => (
                    <SelectItem
                      key={option.partnerCompanyId}
                      value={String(option.partnerCompanyId)}
                    >
                      {option.companyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="의약품명">
              <Input disabled value={data.drugName || '-'} />
            </Field>
            <Field label="월간 처방 예상액">
              <div className="relative">
                <Input
                  className="pr-10"
                  value={
                    expectedAmountEditable
                      ? formatAmountInput(expectedAmount)
                      : data.totalMonthlyExpectedPrescriptionAmount == null
                        ? '-'
                        : formatAmountInput(
                            String(data.totalMonthlyExpectedPrescriptionAmount),
                          )
                  }
                  onChange={(e) => {
                    setExpectedAmount(parseAmountInput(e.target.value));
                    markDirty();
                  }}
                  disabled={!expectedAmountEditable || readOnly}
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  원
                </span>
              </div>
              {!expectedAmountEditable ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  등록된 값이 없을 경우 수정 불가
                </p>
              ) : null}
            </Field>
          </div>

          <Stack gap={12}>
            <ToolbarRow justify="between" gap={8}>
              <p className="text-sm font-medium">거래가능 의약품 코드</p>
              <MenuWriteGate>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={unavailableCodes.length > 0}
                  onClick={() => setDrugModalMode('available')}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  거래 가능 의약품 코드 추가
                </Button>
              </MenuWriteGate>
            </ToolbarRow>
            <DrugCodeTags
              codes={availableCodes}
              readOnly={readOnly}
              onRemove={(code) => {
                setAvailableCodes((prev) => prev.filter((c) => c !== code));
                markDirty();
              }}
            />
          </Stack>

          <Stack gap={12}>
            <ToolbarRow justify="between" gap={8}>
              <p className="text-sm font-medium">거래불가 의약품 코드</p>
              <MenuWriteGate>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={availableCodes.length > 0}
                  onClick={() => setDrugModalMode('unavailable')}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  거래 불가 의약품 코드 추가
                </Button>
              </MenuWriteGate>
            </ToolbarRow>
            <DrugCodeTags
              codes={unavailableCodes}
              readOnly={readOnly}
              onRemove={(code) => {
                setUnavailableCodes((prev) => prev.filter((c) => c !== code));
                markDirty();
              }}
            />
          </Stack>

          {isHospital ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <Field label="의약품 DC 여부">
                <Input disabled value={data.drugDc ? '에' : '아니요'} />
              </Field>
              <Field label="의약품 DC 일정">
                <Input
                  disabled
                  value={
                    data.drugDc
                      ? `${formatDate(data.drugDcFrom)} ~ ${formatDate(data.drugDcTo)}`
                      : '-'
                  }
                />
              </Field>
              <Field label="EDI 수령 가능 여부">
                <Input disabled value={data.ediReceivable ? '가능' : '불가'} />
              </Field>
              <Field label="코딩 가능 월">
                <Input
                  disabled
                  value={formatCodingMonth(data.codingAvailableMonth)}
                />
              </Field>
            </div>
          ) : null}

          <Field label="문전약국 목록">
            <div className="flex min-h-10 flex-wrap gap-2 rounded-lg border p-3">
              {(data.pharmacies?.length ?? 0) === 0 ? (
                <span className="text-sm text-muted-foreground">
                  약국 정보 없음
                </span>
              ) : (
                data.pharmacies.map((pharmacy, index) => (
                  <Badge
                    key={`${pharmacy.pharmacyName}-${pharmacy.pharmacyClosedOn ?? 'open'}-${index}`}
                    variant="outline"
                  >
                    {formatRelatedPharmacyLabel(pharmacy)}
                  </Badge>
                ))
              )}
            </div>
          </Field>

          <div className="grid gap-4 md:grid-cols-2">
            {showDrugApprovalFields ? (
              <Field label="의약품 거래 상태">
                <Select
                  value={drugApprovalStatus}
                  disabled={readOnly}
                  onValueChange={(value) => {
                    setDrugApprovalStatus(value);
                    const mapped = mapDrugApprovalToTransactionStatus(value);
                    if (mapped) setTransactionStatus(mapped);
                    markDirty();
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="거래 상태" />
                  </SelectTrigger>
                  <SelectContent>
                    {DRUG_APPROVAL_STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            ) : null}

            <Field label="제약사 거래 상태">
              <Select
                value={transactionStatus}
                disabled={readOnly}
                onValueChange={(value) => {
                  setTransactionStatus(value);
                  markDirty();
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="거래 상태" />
                </SelectTrigger>
                <SelectContent>
                  {TRANSACTION_STATUS_EDIT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Stack gap={8}>
            <p className="text-sm font-medium">거래 상태 이력</p>
            {statusHistories.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                거래 상태 이력 없음
              </p>
            ) : (
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>거래 상태</TableHead>
                      <TableHead>변경 일시</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {statusHistories.map((item, index) => (
                      <TableRow
                        key={`${item.changedAt}-${item.status}-${index}`}
                      >
                        <TableCell>{formatStatusLabel(item.status)}</TableCell>
                        <TableCell>{formatDateTime(item.changedAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Stack>
        </CardContent>
      </Card>

      <ClientDrugSelectModal
        open={drugModalMode != null}
        pharmaCompanyName={data.pharmaCompanyName}
        refYm={refYm}
        existingDrugCodes={existingDrugCodesForModal}
        onClose={() => setDrugModalMode(null)}
        onConfirm={handleDrugModalConfirm}
      />

      <AlertDialog open={showSaveConfirm} onOpenChange={setShowSaveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>확인</AlertDialogTitle>
            <AlertDialogDescription>
              변경된 내용으로 저장됩니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction onClick={handleSave}>확인</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showExitConfirm} onOpenChange={setShowExitConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>확인</AlertDialogTitle>
            <AlertDialogDescription>
              변경된 내용이 삭제됩니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction onClick={goBack}>확인</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
